/**
 * Frontend Sentry initialization — Phase 2.10 (post-G).
 *
 * Initialized in `main.tsx` BEFORE React mounts so any error during
 * the initial render is captured. The DSN comes from
 * `import.meta.env.VITE_SENTRY_DSN` and is inlined at build time.
 *
 * No-op behavior
 * --------------
 * If `VITE_SENTRY_DSN` is unset / empty, `initSentry()` logs to
 * console and returns. Local dev (no DSN) stays silent.
 *
 * Cross-correlation with backend
 * ------------------------------
 * The backend stamps every response with `X-Request-Id`. The
 * `apiFetch` lib already throws `ApiClientError` on non-2xx; if you
 * wrap that error in a Sentry capture, it should include the
 * requestId in extra context so the engineer can pivot to the
 * backend Sentry event.
 */

// Subpath import: the package index re-exports zod schemas (agent decisions)
// that would otherwise ride in the entry chunk with Sentry's init.
import { scrubSensitive } from '@neuratrade/shared/scrub';
import * as Sentry from '@sentry/react';

let _initialized = false;

export function initSentry(): void {
  if (_initialized) return;
  _initialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // eslint-disable-next-line no-console -- single boot-time line
    console.info('sentry: VITE_SENTRY_DSN unset, error reporting disabled');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    // 0 = errors only (free-tier-friendly). Bump per investigation.
    tracesSampleRate: 0,
    // Don't ship PII; userId is tagged explicitly by the auth hook
    // when a session loads.
    sendDefaultPii: false,
    // NT-Robustez · Fase C — strip PII / secrets (headers, keys, JWTs,
    // ntk_ bridge keys, e-mails) from every event and breadcrumb.
    beforeSend: (event) => scrubSensitive(event),
    beforeBreadcrumb: (crumb) => scrubSensitive(crumb),
  });

  // eslint-disable-next-line no-console -- single boot-time confirmation
  console.info(`sentry: initialized (env=${import.meta.env.MODE})`);
}

/**
 * Tag the current Sentry scope with the authenticated userId. Called
 * from useAuth when a session is acquired or refreshed. Becomes a
 * no-op when Sentry isn't initialized.
 */
export function setSentryUser(userId: string | null): void {
  if (!_initialized) return;
  if (userId) Sentry.setUser({ id: userId });
  else Sentry.setUser(null);
}

/**
 * Adds the backend's requestId to the current scope as a tag. Useful
 * inside `ApiClientError` catch sites so the engineer can pivot from
 * a frontend Sentry event to the matching backend event by the same
 * requestId.
 */
export function tagBackendRequestId(requestId: string | undefined): void {
  if (!_initialized || !requestId) return;
  Sentry.setTag('backendRequestId', requestId);
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Best-effort warning telemetry. When Sentry is initialized, sends a
 * `captureMessage` with level=warning + extra context. Otherwise falls
 * back to `console.warn` so the same call site works in dev without
 * a DSN. Used by the agent-decision WS provider to surface payload
 * validation failures without crashing the UI.
 */
export function captureSentryWarning(message: string, context?: Record<string, unknown>): void {
  if (_initialized) {
    Sentry.captureMessage(message, {
      level: 'warning',
      extra: context,
    });
    return;
  }
  // eslint-disable-next-line no-console -- explicit fallback path
  console.warn(`[warn] ${message}`, context ?? '');
}
